import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {JournalStackParamList} from '@/app/routes';
import {clearLogs} from '@/entities/log/api';
import {parsePeriodKey} from '@/entities/attendance/period';
import {fetchSecuritySettings} from '@/entities/settings/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {Journal} from '@/features/view-journal/ui/Journal';
import {PresenceBoard} from '@/features/view-presence/ui/PresenceBoard';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {ApiError} from '@/shared/api';
import {enumParam, useParamState} from '@/shared/lib/use-param-state';
import {Banner} from '@/shared/ui/banner';
import {AppButton, IconButton} from '@/shared/ui/button';
import {AppInput} from '@/shared/ui/input';
import {Page} from '@/shared/ui/page';
import {Segmented} from '@/shared/ui/segmented';
import {Sheet} from '@/shared/ui/sheet';
import {spacing} from '@/shared/ui/theme';

/**
 * «Журнал» — самостоятельный раздел, а не экран за «Главной».
 *
 * ## Почему он стал вкладкой
 *
 * Проходы — это то, ради чего кабинет открывают чаще всего, и до сих пор путь к ним
 * шёл через главную и ссылку «Все события» в углу карточки. Раздел, который открывают
 * каждый день, обязан быть на панели: «где смотреть, кто прошёл» не должно иметь
 * ответа «сначала на главную».
 *
 * ## Два вопроса, два режима
 *
 * «Кто прошёл» — таблица (см. {@link Journal}). «Кто сейчас внутри» — та же сегодняшняя
 * лента, свёрнутая до строки на человека (`PresenceBoard`). Это разные вопросы к одним
 * и тем же данным, и переключатель между ними назван вопросами, а не словами «лента» и
 * «присутствие».
 *
 * ## Про кнопку с корзиной
 *
 * ⚠️ `DELETE /control/logs` не принимает НИ ОДНОГО фильтра: это вся история компании
 * или ничего, что бы ни стояло в фильтрах на экране. И пароль у неё не от аккаунта, а
 * тот, что задан на терминале, — `GET /control/settings/security` отдаёт только
 * подсказку к нему. Оба факта написаны прямо в листе: разрушительное действие, чья
 * область не совпадает с тем, что видно на экране, — это ловушка.
 *
 * Кнопка поэтому значковая и стоит в стороне от глаголов таблицы, а не рядом с
 * «Скачать»: соседство с ежедневным действием — половина случайных нажатий.
 */
type Props = NativeStackScreenProps<JournalStackParamList, 'Journal'>;

type Mode = 'log' | 'presence';

const MODE_PARAM = enumParam<Mode>(['log', 'presence'], 'log');

const styles = StyleSheet.create({
  toolbar: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  segment: {flex: 1, maxWidth: 420},
  sheetBody: {gap: spacing(3)},
});

export function JournalScreen({navigation, route}: Props) {
  const {t} = useTranslation();
  const {isDesktop} = useBreakpoint();
  const qc = useQueryClient();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const opened = route.params;

  // Вкладка — тоже часть вопроса: ссылка на «кто сейчас на объекте» должна открывать
  // именно её, а не журнал проходов.
  const [mode, setMode] = useParamState('view', 'log' as Mode, MODE_PARAM);
  const [erasing, setErasing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  const security = useQuery({
    queryKey: ['security-settings', companyId],
    queryFn: () => fetchSecuritySettings(companyId as string),
    enabled: !!companyId && erasing,
  });

  const wipe = useMutation({
    meta: {toast: 'toast.journalErased'},
    mutationFn: () => clearLogs(companyId as string, password),
    onSuccess: async () => {
      setConfirming(false);
      setPassword('');
      setErasing(false);
      await qc.invalidateQueries({queryKey: ['journal', companyId]});
      await qc.invalidateQueries({queryKey: ['logs', companyId]});
      await qc.invalidateQueries({queryKey: ['dashboard', companyId]});
    },
  });

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.journal" />;
  }

  // Прокрутка одна и принадлежит странице: список постраничный, а инспектор справа
  // катается сам.
  return (
    <Page
      scroll
      gutter={false}
      title={t('nav.journal')}
      subtitle={t(mode === 'log' ? 'journal.subtitle' : 'presence.subtitle')}
      testID="journal-screen"
      actions={
        <>
          {/* На телефоне «Скачать» живёт в шапке: полоса над таблицей там глаголов
              не несёт. На широком окне та же кнопка стоит в полосе, рядом с
              фильтрами, которые она и выгружает. */}
          {!isDesktop && mode === 'log' ? (
            <IconButton
              icon="download"
              tone="outline"
              size={44}
              label={t('journal.downloadVisible')}
              onPress={() => setExporting(true)}
              testID="journal-download-open"
            />
          ) : null}
          <IconButton
            icon="trash"
            tone="outline"
            size={isDesktop ? 36 : 44}
            label={t('journal.erase')}
            onPress={() => setErasing(true)}
            testID="journal-erase-open"
          />
        </>
      }
      toolbar={
        <View style={styles.toolbar}>
          <Segmented
            style={styles.segment}
            value={mode}
            onChange={setMode}
            testID="journal-mode"
            options={[
              {value: 'log', label: t('journal.modeLog')},
              {value: 'presence', label: t('journal.modePresence')},
            ]}
          />
        </View>
      }>
      {mode === 'presence' ? (
        <PresenceBoard companyId={companyId} />
      ) : (
        <Journal
          /*
            Область приходит параметром маршрута, а внутри журнала она — состояние.
            Ключ пересобирает журнал, когда из отчёта пришли за ДРУГИМ человеком:
            иначе экран, уже смонтированный, просто не заметил бы новые параметры.
          */
          key={`${opened?.recordId ?? ''}|${opened?.period ?? ''}`}
          companyId={companyId}
          scope={
            opened?.recordId
              ? {
                  recordId: opened.recordId,
                  name: opened.name,
                  period: opened.period ? parsePeriodKey(opened.period) : undefined,
                }
              : undefined
          }
          onScopeCleared={() =>
            navigation.setParams({
              recordId: undefined,
              name: undefined,
              period: undefined,
            })
          }
          exportOpen={exporting}
          onExportOpenChange={setExporting}
        />
      )}

      <Sheet
        testID="journal-erase"
        visible={erasing}
        onClose={() => setErasing(false)}
        title={t('journal.erase')}>
        <View style={styles.sheetBody}>
          <Banner
            tone="warning"
            title={t('journal.eraseWarning')}
            message={t('journal.eraseScope')}
          />
          {confirming ? (
            <>
              <AppInput
                label={t('journal.erasePassword')}
                value={password}
                onChangeText={setPassword}
                secure
                hint={
                  security.data?.clear_events_password_hint
                    ? t('journal.passwordHint', {
                        hint: security.data.clear_events_password_hint,
                      })
                    : t('journal.passwordHintMissing')
                }
                showPasswordLabel={t('a11y.showPassword')}
                hidePasswordLabel={t('a11y.hidePassword')}
                testID="journal-erase-password"
              />
              {wipe.isError ? (
                <Banner
                  tone="error"
                  title={
                    wipe.error instanceof ApiError
                      ? wipe.error.message
                      : t('errors.generic')
                  }
                />
              ) : null}
              <AppButton
                title={t('journal.eraseDo')}
                tone="destructive"
                disabled={password.length === 0}
                loading={wipe.isPending}
                onPress={() => wipe.mutate()}
                testID="journal-erase-confirm"
              />
              <AppButton
                title={t('actions.cancel')}
                tone="ghost"
                onPress={() => {
                  setConfirming(false);
                  setPassword('');
                  wipe.reset();
                }}
              />
            </>
          ) : (
            <AppButton
              title={t('journal.erase')}
              tone="outline"
              onPress={() => setConfirming(true)}
              testID="journal-erase-start"
            />
          )}
        </View>
      </Sheet>
    </Page>
  );
}
